"use client";

import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { useAiPrivacyContextSelector } from "@/hooks/useAiPrivacyContextSelector";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { getHTMLFromFragment } from "@knowt/editor/core";
import { Selection } from "@knowt/editor/pm/state";
import { AICompletionType, ItemType, MediaType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import { useStreamingAI } from "@knowt/syncing/hooks/ai/useAI";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import { Editor } from "@tiptap/core";
import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { ChatAgent, ChatFlowState, ChatMessage } from "../types";
import { getListItemPos } from "./utils";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { htmlToMd } from "@knowt/editor/md/parsing/parse";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";

const MAX_CONTEXT_LENGTH = 7;

const ChatEditorCommunicator = createContext<ChatEditorCommunicatorValue | null>(null);

export const ChatEditorCommunicatorProvider = ({
    itemId,
    itemType = ItemType.NOTE,
    isOwner,
    children,
}: {
    itemId?: string | null;
    itemType: ItemType.NOTE | ItemType.FLASHCARDSET;
    isOwner?: boolean;
    children: React.ReactNode;
}) => {
    const [chosenSuggestion, setChosenSuggestion] = useState<string | null>(null);
    const [localEditorAttachments, setLocalEditorAttachments] = useState<LocalMessageAttachment[] | null>(null);
    const [inputPrefix, setInputPrefix] = useState("");
    const [inputQuestion, setInputQuestion] = useState("");
    const [prompt, setPrompt] = useState("");
    const [selectedContent, setSelectedContent] = useState("");
    const [sourceContent, setSourceContent] = useState("");
    const [chatLog, setChatLog] = useState<ChatMessage[]>([]);
    const [streamingText, setStreamingText] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [isEmptyFlashcardSelected, setIsEmptyFlashcardSelected] = useState(false);
    const chatInputRef = useRef<HTMLInputElement>(null);
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

    const [fullContent, setFullContent] = useState<
        string | { term: string | null | undefined; definition: string | null | undefined }[]
    >("");

    const [chatFlowState, setChatFlowState] = useState<ChatFlowState | null>(ChatFlowState.DEFAULT_ACTION);

    const [editorSelection, setEditorSelection] = useState(
        {} as {
            /** remember the editor's selection */
            selection: Selection | null;
            /** remember the editor currently in used (focus) */
            editor: Editor | null;
        }
    );

    /**
     * (only for the flashcard editor usage).
     * to remember, the current flashcard's both term and definition editor
     */
    const [flashcardEditor, setFlashcardEditor] = useState(
        {} as { term: Editor | null; definition: Editor | null; flashcardId: string | null }
    );

    const { checkAuth } = useCheckAuth();
    const { hasReachedAIUsage, user } = useCurrentUser();
    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);
    const setIsAiPrivacyPopupOpen = useAiPrivacyContextSelector(context => context.setIsAiPrivacyPopupOpen);
    const setOpenAiInputMenu = useAiPrivacyContextSelector(context => context.setOpenAiInputMenu);
    const shouldDisplayAiPrivacyPopup = useAiPrivacyContextSelector(context => context.shouldDisplayAiPrivacyPopup);

    const {
        response,
        parsedResponse,
        isStreaming: _isStreaming,
        callAI,
        isDone,
        clearStream,
    } = useStreamingAI({
        itemId: itemId || "",
        itemType,
        type: itemType === ItemType.NOTE ? AICompletionType.NOTE_CHAT : AICompletionType.FLASHCARD_CHAT,
    });

    const rememberSelection = useCallback(
        ({ selection, editor }: { selection: Selection; editor: Editor }) => {
            setEditorSelection({ selection, editor });
        },
        [setEditorSelection]
    );

    const handleUnselect = useCallback(() => {
        const editor = editorSelection.editor;
        editor?.commands?.blur();
        editor?.commands?.setTextSelection(0);

        setSelectedContent("");
        setEditorSelection({ editor: null, selection: null });
        setFlashcardEditor({ term: null, definition: null, flashcardId: null });
        setChatFlowState(ChatFlowState.DEFAULT_ACTION);
        setIsEmptyFlashcardSelected(false);
    }, [
        editorSelection.editor,
        setChatFlowState,
        setEditorSelection,
        setFlashcardEditor,
        setIsEmptyFlashcardSelected,
        setSelectedContent,
    ]);

    const handleSelect = useCallback(
        ({ editor }: { editor: Editor }) => {
            if (itemType === ItemType.FLASHCARDSET) {
                setFlashcardEditor({ term: null, definition: null, flashcardId: null });
            }

            if (!editor) return;

            const {
                state: {
                    doc,
                    selection,
                    selection: { from, to },
                },
            } = editor;

            // `onSelectionUpdate` is called during editor content changed. we don't need to run this. instead only run `handleSelect` on editor's selection updating
            if (from === to) {
                if (selectedContent) {
                    setSelectedContent("");
                }
                return;
            }

            // if the new selection is from another editor, blur and unselect text from the old one
            // biome-ignore lint/suspicious/noDoubleEquals: we expect to compare with null/undefined
            if (editorSelection?.editor && editor != editorSelection.editor) {
                editorSelection.editor.commands.setTextSelection(0);
            }

            rememberSelection({ selection, editor });

            const htmlText = getHTMLFromFragment(doc.slice(from, to).content, editor.schema);

            setSelectedContent(htmlText);

            if (htmlText) {
                setChatFlowState(ChatFlowState.SELECTED_TEXT_ACTIONS);
            } else {
                setChatFlowState(ChatFlowState.DEFAULT_ACTION);
            }
        },
        [editorSelection.editor, rememberSelection, setChatFlowState, setFlashcardEditor, setSelectedContent, itemType]
    );

    const replaceSelectionContent = useCallback(
        ({ content, behavior }: { content: string; behavior: "replace" | "append" | "prepend" | "insertBelow" }) => {
            if (!editorSelection?.selection || !editorSelection?.editor) return;

            const { from, to } = editorSelection.selection || { from: 0, to: 0 };

            if (!from || !to) return;
            if (from === to) return;

            const currentEditor = editorSelection?.editor;

            switch (behavior) {
                case "replace": {
                    const listPos = getListItemPos({ selection: editorSelection?.selection });

                    if (listPos) {
                        // if selection is inside a list, we replace the list instead
                        currentEditor
                            .chain()
                            .setTextSelection({ from: listPos.from, to: listPos.to })
                            .insertContent(content)
                            .run();
                    } else {
                        currentEditor.commands.deleteRange({ from, to });
                        currentEditor.commands.insertContentAt(from, content);
                    }
                    break;
                }
                case "append": {
                    currentEditor.commands.insertContent;
                    break;
                }
                case "prepend":
                    currentEditor.commands.insertContentAt(from, content);
                    break;
                case "insertBelow": {
                    const pos = currentEditor.state.doc.resolve(to);
                    const endOfLine = pos.end();
                    currentEditor.commands.insertContentAt(endOfLine, "\n" + content);
                    break;
                }
            }

            handleUnselect();
        },
        [editorSelection?.editor, editorSelection?.selection, handleUnselect]
    );

    /**
     * special functions to manipulate the content for flashcards editor rather then relying on current's editor selection
     */
    const actionToFlashcard = useCallback(
        ({ term, definition, behavior }: { term: string; definition: string; behavior: "replace" | "insertBelow" }) => {
            const termEditor = flashcardEditor.term;
            const definitionEditor = flashcardEditor.definition;

            if (!termEditor || !definitionEditor) return;

            switch (behavior) {
                case "replace": {
                    termEditor.commands.setContent(term, true);
                    definitionEditor.commands.setContent(definition, true);
                    break;
                }

                case "insertBelow": {
                    const termEndPos = termEditor.state.doc.content.size;
                    const defEndPos = definitionEditor.state.doc.content.size;
                    termEditor.commands.insertContentAt(termEndPos, "\n" + term);
                    definitionEditor.commands.insertContentAt(defEndPos, "\n" + definition);
                }
            }

            handleUnselect();
        },
        [flashcardEditor.definition, flashcardEditor.term, handleUnselect]
    );

    const stagePrompt = useCallback(
        ({ inputQuestion, prompt: _prompt }: { inputQuestion: string; prompt?: string }) => {
            setInputQuestion(inputQuestion);
            if (inputPrefix && !inputQuestion.includes(inputPrefix)) setInputPrefix("");
            if (!inputQuestion && _prompt) setPrompt("");
            if (_prompt) setPrompt(_prompt);
            if (_prompt && inputQuestion) setInputPrefix(inputQuestion);
            if (inputQuestion) chatInputRef.current?.focus();
        },
        [inputPrefix]
    );

    const processChat = useCallback(
        async ({ inputQuestion, prompt, quote }: { inputQuestion?: string; prompt?: string; quote?: string }) => {
            if (!itemId) throw new Error("itemId is required to process chat");
            if (!inputQuestion) throw new Error("inputQuestion and prompt are required to process chat");

            const callChatAI = async () => {
                setIsWaitingForResponse(true);
                setChatFlowState(null);
                setLocalEditorAttachments(null);
                setChatLog([
                    ...chatLog,
                    {
                        type: ChatAgent.USER,
                        text: inputQuestion,
                        prompt,
                        quote,
                        attachments: localEditorAttachments ?? undefined,
                    },
                ]);

                const input = prompt + " " + (inputPrefix ? inputQuestion.replace(inputPrefix, "") : inputQuestion);

                const flashcards =
                    itemType === ItemType.FLASHCARDSET
                        ? (fullContent as { term: string; definition: string }[])
                        : undefined;

                const note =
                    itemType === ItemType.NOTE
                        ? htmlToMd(itemType === ItemType.NOTE ? (fullContent as string) : "")
                        : undefined;

                const context = quote ? htmlToMd(quote) : undefined;

                const messages = chatLog
                    .slice(Math.max(0, chatLog.length - MAX_CONTEXT_LENGTH), chatLog.length)
                    .map(({ type, text, flashcards, attachments }) => ({
                        role: type === ChatAgent.USER ? "user" : ("assistant" as "user" | "assistant"),
                        content: [
                            {
                                type: "text" as const,
                                text: flashcards?.length
                                    ? JSON.stringify(
                                          flashcards.map(f => ({
                                              term: htmlToMd(f.term),
                                              definition: htmlToMd(f.definition),
                                          }))
                                      )
                                    : text,
                            },
                            ...(attachments?.map(a => ({
                                type: a.type,
                                url: a.imageUrl ?? a.url,
                            })) ?? []),
                        ],
                    }));

                const attachments = localEditorAttachments?.map(a => ({
                    type: a.imageUrl ? MediaType.IMAGE : a.type,
                    url: a.imageUrl ?? a.url,
                }));

                callAI({
                    aiParams: {
                        prompt: input,
                        flashcards,
                        note,
                        context,
                        messages,
                        attachments,
                    },
                }).finally(() => {
                    setIsWaitingForResponse(false);
                    stagePrompt({ inputQuestion: "", prompt: "" });
                    setChatFlowState(ChatFlowState.DECIDE_RESPONSE_ACTION);
                });
            };

            const isAuthenticated = await checkAuth({
                event: AuthEvent.CHAT_WITH_KAI,
            });

            if (!isAuthenticated) return;

            if (hasReachedAIUsage("chats")) {
                return openUpgradePopup({
                    event: UpgradeEvent.CHAT_WITH_AI,
                    context: { itemId, itemType: ItemType.MEDIA },
                });
            }

            if (shouldDisplayAiPrivacyPopup()) {
                setIsAiPrivacyPopupOpen(true);
                return setOpenAiInputMenu(() => callChatAI);
            }

            callChatAI();
        },
        [
            itemId,
            checkAuth,
            chatLog,
            callAI,
            inputPrefix,
            itemType,
            fullContent,
            stagePrompt,
            hasReachedAIUsage,
            shouldDisplayAiPrivacyPopup,
            openUpgradePopup,
            setIsAiPrivacyPopupOpen,
            setOpenAiInputMenu,
            user,
            isOwner,
            localEditorAttachments,
        ]
    );

    useEffect(() => {
        if (_isStreaming && !isStreaming) setIsStreaming(true);

        const responseText =
            parsedResponse?.flashcards
                ?.map(
                    (f, index) =>
                        `**Flashcard #${index + 1}** \n**Term:**  ${f.term} \n**Definition:**  ${f.definition}\n\n`
                )
                ?.reduce((acc, curr) => acc + curr, "") ||
            parsedResponse?.note ||
            parsedResponse?.response;

        if (isStreaming && parsedResponse) {
            setStreamingText(responseText);
        }

        if (isDone && response) {
            const fallBackText = "Sorry, I didn't get that. Can you try again?";
            const newChatText = responseText || fallBackText;

            setChatLog([
                ...chatLog,
                {
                    type: ChatAgent.BOT,
                    text: newChatText,
                    flashcards: parsedResponse?.flashcards?.map(f => ({
                        term: getHtmlFromContent({ content: f.term || "" }),
                        definition: getHtmlFromContent({ content: f.definition || "" }),
                    })),
                },
            ]);

            clearStream();
            if (newChatText === fallBackText)
                setChatFlowState(selectedContent ? ChatFlowState.SELECTED_TEXT_ACTIONS : ChatFlowState.DEFAULT_ACTION);
            if (newChatText !== fallBackText) setChatFlowState(ChatFlowState.DECIDE_RESPONSE_ACTION);
            setStreamingText("");
            setIsStreaming(false);
        }

        if (isStreaming && response && isWaitingForResponse) {
            setIsWaitingForResponse(false);
        }
    }, [
        isDone,
        isStreaming,
        response,
        isWaitingForResponse,
        clearStream,
        chatLog,
        setChatFlowState,
        setChatLog,
        _isStreaming,
        setStreamingText,
        setIsStreaming,
        setIsWaitingForResponse,
        parsedResponse,
        itemType,
        selectedContent,
    ]);

    const focusToChatInput = useCallback((delay = 300) => {
        // we put delay since there could case where animation not finished so chatInputRef is undefined
        setTimeout(() => {
            chatInputRef?.current?.focus();
        }, delay);
    }, []);

    return (
        <ChatEditorCommunicator.Provider
            value={{
                selectedContent,
                setSelectedContent,
                inputQuestion,
                prompt,
                chatLog,
                itemType,
                setChatLog,
                flashcardEditor,
                setFlashcardEditor,
                sourceContent,
                setSourceContent,
                fullContent,
                setFullContent,
                stagePrompt,
                handleSelect,
                handleUnselect,
                replaceSelectionContent,
                setChatFlowState,
                actionToFlashcard,
                chatFlowState,
                itemId,
                streamingText,
                isStreaming,
                isWaitingForResponse,
                processChat,
                chatInputRef,
                focusToChatInput,
                setIsEmptyFlashcardSelected,
                isEmptyFlashcardSelected,
                localEditorAttachments,
                setLocalEditorAttachments,
                chosenSuggestion,
                setChosenSuggestion,
            }}>
            {children}
        </ChatEditorCommunicator.Provider>
    );
};

export const useChatEditorCommunicator = <T,>(selector: (value: ChatEditorCommunicatorValue) => T) =>
    useContextSelector(ChatEditorCommunicator, selector);

type ChatEditorCommunicatorValue = {
    /** the text being selected */
    selectedContent: string;
    setSelectedContent: Dispatch<SetStateAction<string>>;
    /** the hidden text in the chat prompt */
    inputQuestion: string;
    /** the text in the chat prompt */
    prompt: string;
    /** list of the chat logs */
    chatLog: ChatMessage[];
    setChatLog: Dispatch<SetStateAction<ChatMessage[]>>;
    /** the current flashcard's term & definition editor in focus */
    flashcardEditor: { term: Editor | null; definition: Editor | null; flashcardId: string | null };
    /** setter for current flashcard's both term & definition editor */
    setFlashcardEditor: Dispatch<
        SetStateAction<{ term: Editor | null; definition: Editor | null; flashcardId: string | null }>
    >;
    /** the full source content , eg: media transcript, note for flashcards*/
    sourceContent: string;
    setSourceContent: Dispatch<SetStateAction<string>>;
    /** the full content for current item, current note/flashcards */
    fullContent: string | { term: string | null | undefined; definition: string | null | undefined }[];
    setFullContent: Dispatch<
        SetStateAction<string | { term: string | null | undefined; definition: string | null | undefined }[]>
    >;
    stagePrompt: ({ inputQuestion, prompt }: { inputQuestion: string; prompt?: string }) => void;
    handleSelect: ({ editor }: { editor: Editor }) => void;
    handleUnselect: () => void;
    replaceSelectionContent: ({
        content,
        behavior,
    }: {
        content: string;
        behavior: "replace" | "append" | "prepend" | "insertBelow";
    }) => void;
    setChatFlowState: Dispatch<SetStateAction<ChatFlowState | null>>;
    chatFlowState: ChatFlowState | null;
    actionToFlashcard: ({
        term,
        definition,
        behavior,
    }: {
        term: string;
        definition: string;
        behavior: "replace" | "insertBelow";
    }) => void;
    itemId?: string | null;
    itemType: ItemType;
    streamingText: string;
    isStreaming: boolean;
    isWaitingForResponse: boolean;
    processChat: ({
        inputQuestion,
        prompt,
        quote,
    }: {
        inputQuestion?: string;
        prompt?: string;
        quote?: string;
    }) => Promise<void>;
    chatInputRef: RefObject<HTMLInputElement | null> | null;
    focusToChatInput: () => void;
    setIsEmptyFlashcardSelected: Dispatch<SetStateAction<boolean>>;
    isEmptyFlashcardSelected: boolean;
    localEditorAttachments: LocalMessageAttachment[] | null;
    setLocalEditorAttachments: Dispatch<SetStateAction<LocalMessageAttachment[] | null>>;
    chosenSuggestion: string | null;
    setChosenSuggestion: Dispatch<SetStateAction<string | null>>;
};
